import { GetServerSideProps } from 'next'
import Script from 'next/script'
import Head from 'next/head'
import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import { FormProvider } from 'react-hook-form'
import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { InView } from 'react-intersection-observer'

import Meta from '@/components/Meta'
import DefaultLayout from '@/components/layouts/Default'
import { loadTranslationsWithMarket } from '@/utils/loadTranslations'
import { useSearchNearParking } from '@/components/home/SearchNearParking/hooks/useSearchNearParking'
import { useMarket } from '@/utils/multi-markets/context'
import HeroHome from '@/components/home/HeroHome/HeroHome'
import SectionPlaceholder from '@/components/common/SectionPlaceholder'
import FaqSection from '@/components/sections/Faq'
import HaveParking from '@/components/sections/HaveParking'

const Trending = dynamic(() => import('@/components/home/Trending/Trending'), {
  ssr: false
})
const ParkingSection = dynamic(() => import('@/components/home/Parking'), {
  ssr: false
})
const ContactSection = dynamic(
  () => import('@/components/sections/ContactSection'),
  { ssr: false }
)
const Partners = dynamic(() => import('@/components/sections/Partners'), {
  ssr: false
})
const PopupOverlayView = dynamic(
  () => import('@/components/Popups/PopupOverlayView'),
  { ssr: false }
)
const PopupCpkView = dynamic(
  () => import('@/components/Popups/PopupCpk/PopupCpkView'),
  { ssr: false }
)

interface HomeProps {
  host: string
}

export const getServerSideProps: GetServerSideProps<HomeProps> = async ctx => {
  const baseProps = await loadTranslationsWithMarket(ctx, {
    cache: 'stale-while-revalidate',
    namespaces: ['home', 'common', 'popups', 'parkings', 'faq']
  })

  const protocol = ctx.req.headers['x-forwarded-proto'] || 'https'
  const host = `${protocol}://${ctx.req.headers.host}`

  return {
    ...baseProps,
    props: {
      ...(baseProps as any).props,
      host
    }
  }
}

export default function Home({ host }: HomeProps) {
  const { t } = useTranslation('home')
  const { form, onSubmit } = useSearchNearParking({})
  const [isClient, setIsClient] = useState(false)
  const { market } = useMarket()
  const { query } = useRouter()

  useEffect(() => {
    if (query.recommendationSource) {
      document.cookie = `recommendationSource=${query.recommendationSource}`
    }
    setIsClient(true)
  }, [query])

  if (!isClient) return null

  return (
    <>
      <DefaultLayout fullPage>
        <Script
          id="schema-corporation"
          type="application/ld+json"
          strategy="afterInteractive"
        >
          {JSON.stringify({
            '@context': 'http://schema.org',
            '@type': 'Corporation',
            'name': 'NaviParking',
            'url': host,
            'logo': `${host}/images/logo.png`
          })}
        </Script>
        <Script
          id="schema-localbusiness"
          type="application/ld+json"
          strategy="afterInteractive"
        >
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'LocalBusiness',
            'name': 'NaviParking',
            'image': `${host}/images/logo.png`,
            '@id': host,
            'url': host,
            'telephone': '+48 519 879 211',
            'address': {
              '@type': 'PostalAddress',
              'streetAddress': 'Chmielna 73',
              'addressLocality': 'Warszawa',
              'postalCode': '00-801',
              'addressCountry': 'PL'
            },
            'sameAs': [
              'https://www.facebook.com/NaviParking',
              'https://www.linkedin.com/company/naviparking'
            ]
          })}
        </Script>

        <Meta
          title={t('meta.title')}
          description={t('meta.description')}
          image={`${host}/images/og-image.webp`}
          url={host}
          canonical={host}
          lang={market === 'ae' ? 'en' : market === 'bh' ? 'ar' : 'pl'}
        />

        <Head>
          <link
            rel="preload"
            as="image"
            href={`https://cdn.naviparking.com/digital_parking/hero_image/${
              market === 'pl' ? 'pl' : 'bh'
            }/home_background.webp`}
            fetchPriority="high"
          />
        </Head>
        <div id="popup-wrapper" />
        <main style={{ contain: 'layout' }}>
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(data => onSubmit(data, true))}>
              <HeroHome control={form.control} />
            </form>
          </FormProvider>

          <div style={{ contain: 'layout' }}>
            <InView triggerOnce rootMargin="200px">
              {({ ref, inView }) => (
                <div ref={ref}>
                  {inView ? (
                    <Trending />
                  ) : (
                    <SectionPlaceholder
                      height="400px"
                      testId="trending-placeholder"
                    />
                  )}
                </div>
              )}
            </InView>
          </div>

          <div style={{ contain: 'layout' }}>
            <InView triggerOnce rootMargin="200px">
              {({ ref, inView }) => (
                <div ref={ref}>
                  {inView ? (
                    <ParkingSection />
                  ) : (
                    <SectionPlaceholder
                      height="500px"
                      testId="parkingSection-placeholder"
                    />
                  )}
                </div>
              )}
            </InView>
          </div>

          <div style={{ contain: 'layout' }}>
            <InView triggerOnce rootMargin="200px">
              {({ ref, inView }) => (
                <div ref={ref}>
                  <FaqSection dataTestId="home-faq" forHomePage />
                </div>
              )}
            </InView>
          </div>

          <div style={{ contain: 'layout' }}>
            <InView triggerOnce rootMargin="200px">
              {({ ref, inView }) => (
                <div ref={ref}>
                  {inView ? (
                    <ContactSection />
                  ) : (
                    <SectionPlaceholder
                      height="400px"
                      testId="contactSection-placeholder"
                    />
                  )}
                </div>
              )}
            </InView>
          </div>

          <div style={{ contain: 'layout' }}>
            <InView triggerOnce rootMargin="200px">
              {({ ref, inView }) => (
                <div ref={ref}>
                  {inView ? (
                    <Partners />
                  ) : (
                    <SectionPlaceholder
                      height="300px"
                      testId="partners-placeholder"
                    />
                  )}
                </div>
              )}
            </InView>
          </div>

          <div style={{ contain: 'layout' }}>
            <InView triggerOnce rootMargin="200px">
              {({ ref, inView }) => (
                <div ref={ref}>
                  <HaveParking />
                </div>
              )}
            </InView>
          </div>
        </main>

        {market && market !== 'bh' && <PopupOverlayView />}
        {market !== 'bh' && <PopupCpkView />}
      </DefaultLayout>
      <div id="modal-search-mobile" />
    </>
  )
}
